import { Component, Property, MeshComponent } from '@wonderlandengine/api';
import { quat, vec3 } from 'gl-matrix';

/**
 * vr-controls.js — Full VR locomotion and interaction for Oasis/DeadVox
 * Updated to align with movement.js architecture for reliable WebXR performance.
 */

export class VRControls extends Component {
    static TypeName = 'vr-controls';

    static Properties = {
        playerRig:        Property.object(),
        treadmillObject:  Property.object(),
        pistolObject:     Property.object(),
        miningToolObject: Property.object(),
        miningToolOrigin: Property.object(), // NEW: Align aim with visual barrel
        miningToolTarget: Property.object(), // NEW: Align aim with visual barrel
        leftInput:        Property.object(),
        rightInput:       Property.object(),
        turnSpeed:        Property.float(90.0),
        deadzone:         Property.float(0.18),
    };

    // ── Lifecycle ──────────────────────────────────────────────────────────────

    init() {
        this._vt        = null;  
        this._pistol    = null;  
        this._miningTool = null;
        this._inputL    = null;  
        this._inputR    = null;  

        this._keys = {
            w: false, a: false, s: false, d: false,
            ' ': false, shift: false,
        };

        this._prevA            = false;
        this._prevB            = false;
        this._prevYL           = false;
        this._prevTrigL        = false;
        this._prevGripL        = false;
        this._prevThumbL       = false;

        this._trigLMiningHeld  = false; // true while left trigger held in mining mode

        this._activeToolId = 'mining';
        this._triggerRPressed = false;
        this._isCraftingOpen  = false;

        // Smoothed left-stick axes for fluid VR locomotion
        this._smoothAxX = 0;
        this._smoothAxZ = 0;

        this._controllersInitialized = false;
        
        // Pre-allocate math vectors to eliminate garbage collection pauses
        this._tempRigRot = quat.create();
        this._tempDeltaRot = quat.create();
        this._up = new Float32Array([0, 1, 0]);

        this._miningMeshes = null;
        this._pistolMeshes = null;
    }

    start() {
        this._resolveStaticComponents();
        window.VRControls = this;

        // Mirror ArenaWars: hook engine session events so controller refs are
        // re-resolved on every XR session start and cleaned up on session end.
        if (this.engine?.onXRSessionStart) {
            this._boundOnXRSessionStart = this._onXRSessionStart.bind(this);
            this.engine.onXRSessionStart.add(this._boundOnXRSessionStart);
        }
        if (this.engine?.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this._onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
    }

    onDestroy() {
        if (this._boundOnXRSessionStart && this.engine?.onXRSessionStart) {
            this.engine.onXRSessionStart.remove(this._boundOnXRSessionStart);
        }
        if (this._boundOnXRSessionEnd && this.engine?.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
    }

    _onXRSessionStart() {
        // Invalidate cached WLE input components — xrInputSource refs change each session.
        // update() will re-poll _resolveInputs() until both components are ready.
        this._controllersInitialized = false;
        this._inputL = null;
        this._inputR = null;

        // Reset crafting-open flag so a stale true from a previous session can't
        // permanently block turning (early return in _handleRightController).
        this._isCraftingOpen = false;
        if (window.TextMenuHud) {
            window.TextMenuHud._craftingOpen = false;
            window.TextMenuHud._isCrafting   = false;
        }

        // Clean up any desktop mining state that wasn't stopped before entering VR.
        // Without this, _isMining=true carried over from desktop silently blocks
        // the next startMining() call (early-return guard), leaving the desktop
        // laser active while the VR laser is never shown.
        try { this._miningTool?.stopMining?.(); } catch (_) {}
    }

    _onXRSessionEnd() {
        this._flushKeys();
        this._controllersInitialized = false;
        this._inputL = null;
        this._inputR = null;
    }

    onDeactivate() {
        this._flushKeys();
    }

    // ── Per-frame ──────────────────────────────────────────────────────────────

    update(dt) {
        if (!this._isXRActive()) return;

        // Try to resolve WLE input components; never blocks movement if they're missing
        if (!this._controllersInitialized) {
            this._resolveInputs();
        }

        if (window.GameState !== 'playing') return;

        const session = this.engine.xr?.session;
        if (!session) return;

        // Always prefer the live session.inputSources — WLE xrInputSource refs go stale
        // after a controller sleep/reconnect and report 0 axes while remaining non-null,
        // which would silently block the fallback. session.inputSources is always fresh.
        let resolvedGpL = null;
        let resolvedGpR = null;
        for (const src of (session.inputSources ?? [])) {
            const gp = src?.gamepad;
            if (!gp) continue;
            if (!resolvedGpL && src.handedness === 'left')  resolvedGpL = gp;
            if (!resolvedGpR && src.handedness === 'right') resolvedGpR = gp;
        }
        // Fall back to WLE input components only when session gave nothing
        if (!resolvedGpL) resolvedGpL = this._inputL?.xrInputSource?.gamepad ?? null;
        if (!resolvedGpR) resolvedGpR = this._inputR?.xrInputSource?.gamepad ?? null;

        if (!resolvedGpL && !resolvedGpR) return;

        this._keys.w = false;
        this._keys.a = false;
        this._keys.s = false;
        this._keys.d = false;
        this._keys[' '] = false;
        this._keys.shift = false;

        let jumpPressed = false;
        let cycleMat    = false;

        if (resolvedGpL) {
            this._handleLeftController(resolvedGpL, dt);
            if (!this._isCraftingOpen && this._readButton(resolvedGpL, 4)) jumpPressed = true;

            const yNow    = this._readButton(resolvedGpL, 5);
            if (yNow && !this._prevYL) this._switchTool();
            this._prevYL = yNow;

            // Left grip (button 1) → toggle crafting panel
            const gripL = this._readButton(resolvedGpL, 1);
            if (gripL && !this._prevGripL) this._toggleVRCrafting();
            this._prevGripL = gripL;

            // Left thumbstick click (button 2) → use shield cell
            const thumbL = this._readButton(resolvedGpL, 2);
            if (thumbL && !this._prevThumbL) window.GameInterface?.useShieldCell?.();
            this._prevThumbL = thumbL;
        }

        if (resolvedGpR) {
            this._handleRightController(resolvedGpR, dt);
            const aNow = this._readButton(resolvedGpR, 4);
            if (this._trigLMiningHeld && aNow && !this._prevA) {
                // Left trigger preview + A press = cycle mineral material
                if (this._vt) {
                    const cur = this._vt.buildMaterialId ?? 1;
                    let next = cur + 1; if (next > 5) next = 1;
                    this._vt.buildMaterialId = next;
                    try { window.GameInterface?.updateActiveBlueprint?.(next); } catch (_) {}
                }
            } else if (!this._trigLMiningHeld && aNow) {
                jumpPressed = true;
            }
            this._prevA = aNow;
            const bNow = this._readButton(resolvedGpR, 5);
            if (bNow && !this._prevB) cycleMat = true;
            this._prevB = bNow;
        }

        this._keys[' '] = jumpPressed;

        if (this._vt && typeof this._vt.setKeys === 'function') {
            this._vt.setKeys(this._keys);
        }

        // Sync active weapon to right controller every frame.
        // setPositionWorld/setRotationWorld correctly computes the local transform
        // relative to any parent (including camera) each frame, so the weapon
        // always appears at the controller without changing the object hierarchy.
        // (Reparenting is avoided because it can deactivate fps-pistol.update(),
        // preventing cooldown from ticking and permanently jamming the gun.)
        this._syncWeaponToController();

        if (cycleMat && this._vt) {
            const cur = this._vt.buildMaterialId ?? 1;
            let next = cur + 1; if (next > 5) next = 1;
            this._vt.buildMaterialId = next;
            try { window.GameInterface?.updateActiveBlueprint?.(next); } catch (_) {}
        }
    }

    // ── Controller handlers ────────────────────────────────────────────────────

    _handleLeftController(gp, dt) {
        const dz = this.deadzone;

        // Try axes[2/3] (xr-standard) and axes[0/1] (some native profiles); use whichever has greater magnitude.
        const _lax2 = this._axis(gp, 2), _lax0 = this._axis(gp, 0);
        const _laz3 = this._axis(gp, 3), _laz1 = this._axis(gp, 1);
        const rawAxX = Math.abs(_lax2) >= Math.abs(_lax0) ? _lax2 : _lax0;
        const rawAxZ = Math.abs(_laz3) >= Math.abs(_laz1) ? _laz3 : _laz1;

        // Smooth axes with 80ms time constant — removes jitter without perceptible lag
        const alpha = Math.min(1, dt / 0.08);
        this._smoothAxX += (rawAxX - this._smoothAxX) * alpha;
        this._smoothAxZ += (rawAxZ - this._smoothAxZ) * alpha;
        const axX = this._smoothAxX, axZ = this._smoothAxZ;

        if (axZ < -dz) this._keys.w = true;
        if (axZ >  dz) this._keys.s = true;
        if (axX < -dz) this._keys.a = true;
        if (axX >  dz) this._keys.d = true;

        // Sprint when stick nearly maxed out
        const stickMag = Math.hypot(axX, axZ);
        if (stickMag > 0.85) this._keys.shift = true;

        // Analog speed scale: remap [deadzone..1] → [0..1] for proportional movement feel
        const moveScale = Math.min(1, Math.max(0, (stickMag - dz) / (1 - dz)));
        if (this._vt) this._vt.setVRMoveScale(moveScale);

        const trigL = (gp.buttons[0]?.value ?? 0) > 0.5;

        // While the crafting panel is open the left trigger cycles recipes.
        // Movement axes above still run so the player can reposition.
        if (this._isCraftingOpen) {
            if (trigL && !this._prevTrigL) {
                window.TextMenuHud?.cycleCraftRecipe?.();
            }
            this._prevTrigL = trigL;
            this._trigLMiningHeld = false;
            return;
        }

        if (this._activeToolId === 'mining') {
            if (trigL) {
                this._miningTool?.setBuildPreviewing?.(true);
                this._trigLMiningHeld = true;
            } else {
                this._trigLMiningHeld = false;
                if (this._prevTrigL) {
                    this._miningTool?.build?.();
                    this._miningTool?.setBuildPreviewing?.(false);
                }
            }
        } else {
            this._trigLMiningHeld = false;
        }
        if (this._activeToolId === 'pistol') {
            if (trigL && !this._prevTrigL) {
                this._pistol?.reload?.();
            }
        }
        this._prevTrigL = trigL;
    }

    _toggleVRCrafting() {
        this._isCraftingOpen = !this._isCraftingOpen;
        if (window.GameInterface?.toggleCraftingPanel) {
            // Lazily wrap the panel callback so any external close (e.g. a "×" UI
            // button calling GameInterface.toggleCraftingPanel(false)) syncs our
            // flag back to false.  Without this, _isCraftingOpen stays true after
            // an external close and the early return in _handleRightController
            // permanently blocks turning until the next grip press.
            if (!this._craftingPanelWrapped) {
                const orig = window.GameInterface.toggleCraftingPanel;
                window.GameInterface.toggleCraftingPanel = (open) => {
                    this._isCraftingOpen = !!open;
                    orig(open);
                };
                this._craftingPanelWrapped = true;
            }
            window.GameInterface.toggleCraftingPanel(this._isCraftingOpen);
        }
    }

    _handleRightController(gp, dt) {
        const dz = this.deadzone;

        // Late-resolve tool components if they were on inactive objects at start() time
        if (!this._pistol    && this.pistolObject)     this._pistol    = this.pistolObject.getComponent('fps-pistol');
        if (!this._miningTool && this.miningToolObject) this._miningTool = this.miningToolObject.getComponent('mining-tool');

        // While crafting panel is open, right trigger crafts the highlighted recipe.
        // TextMenuHud owns which recipe is selected; we just fire the id it gives us.
        if (this._isCraftingOpen) {
            const trigR = (gp.buttons[0]?.value ?? 0) > 0.5;
            if (trigR && !this._triggerRPressed) {
                const recipeId = window.TextMenuHud?.currentRecipeId?.();
                if (recipeId) window.GameInterface?.craft?.(recipeId);
            }
            this._triggerRPressed = trigR;
            return;
        }

        // ── Smooth turn ──────────────────────────────────
        // Try axes[2] (xr-standard) then axes[0] (some native profiles); use whichever has greater magnitude.
        const _ax2 = this._axis(gp, 2);
        const _ax0 = this._axis(gp, 0);
        const axX  = Math.abs(_ax2) >= Math.abs(_ax0) ? _ax2 : _ax0;
        if (Math.abs(axX) > dz && this._vt) {
            const magnitude  = (Math.abs(axX) - dz) / (1.0 - dz);
            const turnRad    = Math.sign(axX) * magnitude * (this.turnSpeed * Math.PI / 180) * dt;
            this._vt._lookYaw -= turnRad;
            // Guard against playerRig being unassigned — rotation fails silently so
            // the trigger code below always runs.
            try { this.playerRig?.rotateAxisAngleRad(this._up, -turnRad); } catch (_) {}
        }

        const trigR = (gp.buttons[0]?.value ?? 0) > 0.5;

        if (this._activeToolId === 'mining') {
            if (this._miningTool) {
                if (trigR) this._miningTool.startMining();
                else       this._miningTool.stopMining();
            }
        } else if (this._activeToolId === 'pistol') {
            if (this._pistol) {
                if (trigR) this._pistol.pullTrigger?.();
                else       this._pistol.releaseTrigger?.();
            }
        }
        this._triggerRPressed = trigR;
    }

    // ── Helpers ────────────────────────────────────────────────────────────────

    _syncWeaponToController() {
        if (!this.rightInput) return;
        const toolObj = this._activeToolId === 'pistol'  ? this.pistolObject
                      : this._activeToolId === 'mining' ? this.miningToolObject
                      : null;
        if (!toolObj) return;
        // setPositionWorld/setRotationWorld compute the correct local transform
        // relative to the object's current parent each frame, so this works even
        // when the pistol is a child of the desktop camera.
        toolObj.setPositionWorld(this.rightInput.getPositionWorld([]));
        toolObj.setRotationWorld(this.rightInput.getRotationWorld([]));
    }

    _setMeshesActive(toolObj, active, meshCache) {
        if (!toolObj) return meshCache;
        if (!meshCache) {
            meshCache = toolObj.getComponents(MeshComponent);
            // Also grab children meshes just in case the setup is nested
            const children = toolObj.children;
            for(let i=0; i < children.length; i++) {
                const childMeshes = children[i].getComponents(MeshComponent);
                meshCache = meshCache.concat(childMeshes);
            }
        }
        for (let i = 0; i < meshCache.length; i++) {
            meshCache[i].active = active;
        }
        return meshCache;
    }

    _switchTool() {
        const tools = ['mining', 'pistol'];
        let idx = tools.indexOf(this._activeToolId);
        idx = (idx + 1) % tools.length;
        const next = tools[idx];

        this._activeToolId = next;

        // Always toggle WLE object states so components receive update() calls.
        // GameInterface is notified separately as a side-effect, not the gating condition.
        const isMining = next === 'mining';
        const isPistol = next === 'pistol';
        this._miningMeshes = this._setMeshesActive(this.miningToolObject, isMining, this._miningMeshes);
        this._pistolMeshes = this._setMeshesActive(this.pistolObject,     isPistol, this._pistolMeshes);
        if (this.miningToolObject) this.miningToolObject.active = isMining;
        if (this.pistolObject)     this.pistolObject.active     = isPistol;

        // Late-resolve components in case objects were inactive at start() time
        if (!this._pistol && this.pistolObject)
            this._pistol = this.pistolObject.getComponent('fps-pistol');
        if (!this._miningTool && this.miningToolObject)
            this._miningTool = this.miningToolObject.getComponent('mining-tool');

        if (next !== 'pistol' && this._pistol) this._pistol.releaseTrigger?.();
        if (next !== 'mining' && this._miningTool) this._miningTool.stopMining();

        // Do NOT call selectWeapon here — that callback is wired to PlayerInput._applySelection()
        // which activates desktop tool objects (with their own laser meshes) causing a second
        // laser to appear in VR alongside the controller-positioned one.
        try { window.GameInterface?.setSelectedWeapon?.(next); } catch (_) {}
    }

    _readButton(gp, index) {
        const b = gp.buttons?.[index];
        if (!b) return false;
        return typeof b === 'object' ? b.pressed : !!b;
    }

    _axis(gp, index) {
        return gp.axes?.[index] ?? 0;
    }

    _isXRActive() {
        return !!(this.engine?.xr?.session);
    }

    _flushKeys() {
        this._keys.w = false; this._keys.a = false;
        this._keys.s = false; this._keys.d = false;
        this._keys[' '] = false; this._keys.shift = false;
        if (this._vt && typeof this._vt.setKeys === 'function') {
            this._vt.setKeys(this._keys);
        }
        if (this._pistol) this._pistol.releaseTrigger?.();
    }

    _resolveStaticComponents() {
        if (this.treadmillObject) {
            this._vt = this.treadmillObject.getComponent('voxel-treadmill');
        }
        if (!this._vt && window.VoxelTreadmill) {
            this._vt = window.VoxelTreadmill;
        }
        if (this.pistolObject) {
            this._pistol = this.pistolObject.getComponent('fps-pistol');
            if (this._pistol) {
                this._pistol.vrMuzzleOrigin = this.miningToolOrigin;
                this._pistol.vrMuzzleTarget = this.miningToolTarget;
            }
        }
        if (this.miningToolObject) {
            this._miningTool = this.miningToolObject.getComponent('mining-tool');
            if (this._miningTool) {
                this._miningTool.vrMuzzleOrigin = this.miningToolOrigin;
                this._miningTool.vrMuzzleTarget = this.miningToolTarget;
            }
        }
    }

    _resolveInputs() {
        if (this.leftInput && this.rightInput) {
            this._inputL = this.leftInput.getComponent('input');
            this._inputR = this.rightInput.getComponent('input');
            if (this._inputL && this._inputR) {
                this._controllersInitialized = true;
            }
        }
    }
}